import React, { createContext, useContext, useState, useEffect } from 'react'
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles'
import lightTheme from './lightTheme'

interface ThemeContextType {
  isDarkMode: boolean
  toggleTheme: () => void
  theme: Theme & Record<string, any>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useThemeCustom = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('theme') === 'true'
  })
  const [theme, setTheme] = useState<Theme & Record<string, any>>(lightTheme)

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode)
  }

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode.toString())
  }, [isDarkMode])

  useEffect(() => {
    let cancelled = false

    if (!isDarkMode) {
      setTheme(lightTheme)
      return
    }

    import('./darkTheme').then((mod) => {
      if (!cancelled) {
        setTheme(mod.default)
      }
    })

    return () => {
      cancelled = true
    }
  }, [isDarkMode])

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        theme,
      }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
